import { useState } from 'react';

export interface FilterGroup {
  key: string;
  label: string;
  options: string[];
  value: string;
}

interface FilterBarProps {
  groups: FilterGroup[];
  onChange: (key: string, value: string) => void;
  onReset: () => void;
  activeCount: number;
}

export default function FilterBar({ groups, onChange, onReset, activeCount }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 lg:hidden">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex h-9 cursor-pointer items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3.5 text-[13px] text-foreground-800"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-filter-3-line text-sm" />
          </span>
          筛选条件
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-medium text-primary-950">
              {activeCount}
            </span>
          )}
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? 'rotate-180' : ''}`} />
          </span>
        </button>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onReset}
            className="flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 text-xs text-foreground-500 transition-colors hover:text-primary-700"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-refresh-line text-xs" />
            </span>
            重置
          </button>
        )}
      </div>

      <div className={`${open ? 'mt-3 block' : 'hidden'} lg:mt-0 lg:block`}>
        {groups.map((group) => (
          <div
            key={group.key}
            className="flex flex-col gap-2 border-t border-background-200 py-3 first:border-t-0 first:pt-0 lg:flex-row lg:items-center lg:gap-4"
          >
            <span className="w-[56px] shrink-0 text-[12px] text-foreground-500">{group.label}</span>
            <div className="flex flex-wrap gap-1.5 md:gap-2">
              {['全部', ...group.options].map((option) => {
                const actualValue = option === '全部' ? '' : option;
                const active = group.value === actualValue;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => onChange(group.key, actualValue)}
                    className={`h-8 cursor-pointer whitespace-nowrap rounded-full px-3 text-[12px] transition-colors ${
                      active
                        ? 'bg-primary-600 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="hidden items-center justify-between border-t border-background-200 pt-3 lg:flex">
          <span className="text-[11px] text-foreground-400">
            已选择 {activeCount} 个筛选条件
          </span>
          <button
            type="button"
            onClick={onReset}
            disabled={activeCount === 0}
            className={`flex h-8 items-center gap-1 whitespace-nowrap rounded-full px-3 text-xs transition-colors ${
              activeCount === 0
                ? 'cursor-not-allowed text-foreground-300'
                : 'cursor-pointer text-foreground-600 hover:bg-background-100 hover:text-primary-700'
            }`}
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-refresh-line text-xs" />
            </span>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}